import type {ReactElement} from 'react';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import type {AuthSsoButtonProps} from './auth-sso-button.types';
import styles from './auth-sso-button.module.scss';

/**
 * The shared shell for every authentication provider button on the login page — Google, Apple,
 * Facebook and student. The design draws all four identically, so they share one presentational
 * component; each caller keeps its own sign-in logic and passes only a label, an icon and a handler.
 *
 * `ButtonType.SECONDARY` at `ButtonSize.MEDIUM` already matches the design exactly — 40px height,
 * `$br-neutral-primary` border, 6px icon gap, `body-md-bold` label, 16px icon — so the stylesheet only
 * sizes the brand marks at `ButtonSize.LARGE`.
 *
 * `-noun` is required, not decorative: `.btn` lowercases its label and re-capitalises only
 * `::first-letter`, which would render "Log in with Google" as "Log in with google". Every label here
 * ends in a proper noun.
 */
export function AuthSsoButton({text, onClick, icon = '', customIcon, size = ButtonSize.MEDIUM, ...props}: AuthSsoButtonProps): ReactElement {
  return (
    <Button
      customClasses={`-noun ${styles.authSsoButton}`}
      customIcon={customIcon}
      data-testid={props['data-testid']}
      icon={icon}
      isFullWidth
      onClick={onClick}
      size={size}
      text={text}
      type={ButtonType.SECONDARY}
    />
  );
}
